// utils/tablaPaginacion.ts
//
// EL SELECTOR DE "N POR PÁGINA" QUE SÍ FUNCIONA.
//
// ── El bug que este helper existe para no repetir ───────────────────────────
//
// Trece tablas del sistema tenían su paginación escrita así:
//
//     pagination={{ pageSize: 20, showSizeChanger: true }}
//
// Se ve perfectamente razonable y está roto. En Ant Design, `pagination.pageSize`
// es una prop CONTROLADA: `rc-pagination` la pasa como `value` a
// `useMergedState`, así que el estado interno que escribe el selector se
// descarta en el render siguiente y el número vuelve al literal. El desplegable
// cambia de "20" a "50" y la tabla sigue mostrando 20 filas.
//
// Nadie lo reporta como error porque no falla nada: simplemente no pasa lo que
// se pidió. Y como el desplegable sí muestra el número nuevo, uno duda de si lo
// apretó bien.
//
// La forma correcta para una tabla que pagina en el NAVEGADOR es
// `defaultPageSize`: deja el valor como no controlado y el selector escribe de
// verdad. Este helper lo encapsula para que la próxima tabla no vuelva a
// nacer rota.
//
// ── Cuándo NO usar esto ─────────────────────────────────────────────────────
//
// Solo sirve para tablas que reciben TODAS las filas y paginan en el navegador.
// Si la tabla pagina en el SERVIDOR (manda `page`/`size` en la request), la
// paginación tiene que ser controlada de verdad: `current`, `pageSize`, `total`
// y un `onChange: (pagina, tamano) => ...` que use LOS DOS argumentos. Ahí el
// error simétrico es escribir `onChange: (p) => cargar(p)`, que ignora el
// tamaño nuevo y deja el selector igual de muerto.

/** Opciones por defecto. Cubren desde una tabla corta hasta una de auditoría. */
const OPCIONES_POR_DEFECTO = [10, 25, 50, 100];

export interface OpcionesPaginacion {
  /** Filas por página al abrir. */
  porDefecto?: number;
  /** Tamaños ofrecidos en el desplegable. */
  opciones?: number[];
  /**
   * Cómo se llama una fila, para el texto "1–20 de 57 propiedades".
   * `[singular, plural]`, o una sola palabra si el plural es +"s".
   */
  etiqueta?: string | [string, string];
  /** Oculta el paginador entero cuando todo entra en una página. */
  ocultarSiUnaPagina?: boolean;
  /** Tamaño visual del paginador, para tablas `size="small"`. */
  size?: "small" | "default";
}

/**
 * Configuración de paginación para una tabla que pagina en el NAVEGADOR.
 *
 * @example
 *   <Table pagination={paginacionCliente({ porDefecto: 20, etiqueta: "alerta" })} />
 */
export function paginacionCliente(opciones: OpcionesPaginacion = {}) {
  const {
    porDefecto = 25,
    opciones: tamanos = OPCIONES_POR_DEFECTO,
    etiqueta,
    ocultarSiUnaPagina = false,
    size,
  } = opciones;

  const [singular, plural] = Array.isArray(etiqueta)
    ? etiqueta
    : etiqueta
      ? [etiqueta, `${etiqueta}s`]
      : ["", ""];

  return {
    // `defaultPageSize` y NO `pageSize`: ver el comentario de arriba. Este es
    // el detalle del que depende que el selector haga algo.
    defaultPageSize: porDefecto,
    showSizeChanger: true,
    // Se pasan siempre para que el tamaño de arranque esté entre las opciones:
    // si no, el desplegable abre mostrando un valor que no puede volver a
    // elegirse después de cambiarlo.
    pageSizeOptions: tamanos.includes(porDefecto)
      ? tamanos
      : [...tamanos, porDefecto].sort((a, b) => a - b),
    // "1–20 de 57" — la pregunta que uno tiene mirando una tabla paginada es
    // cuánto hay en total, y sin esto hay que ir hasta la última página para
    // saberlo.
    showTotal: (total: number, rango: [number, number]) =>
      singular
        ? `${rango[0]}–${rango[1]} de ${total} ${total === 1 ? singular : plural}`
        : `${rango[0]}–${rango[1]} de ${total}`,
    ...(ocultarSiUnaPagina ? { hideOnSinglePage: true } : {}),
    ...(size ? { size } : {}),
  };
}

export interface OpcionesPaginacionServidor
  extends Omit<OpcionesPaginacion, "porDefecto" | "size" | "ocultarSiUnaPagina"> {
  /** Página actual (1-based), tal como la tiene el estado que hace la request. */
  page: number;
  /** Filas por página vigentes. Acá `size` es el TAMAÑO DE PÁGINA, no el visual. */
  size: number;
  /** Total de filas del conjunto COMPLETO, no de la página. */
  total: number;
  /**
   * Recibe la página Y el tamaño nuevos. Los DOS argumentos son obligatorios:
   * escribir `(p) => cargar(p)` descarta el tamaño y deja el selector muerto —
   * es el mismo bug que `pageSize` literal, por el otro lado.
   */
  onChange: (pagina: number, tamano: number) => void;
}

/**
 * Configuración de paginación para una tabla que pagina en el SERVIDOR.
 *
 * Acá `pageSize` SÍ va controlado (es el que se mandó en la request), y por eso
 * el `onChange` tiene que propagar el tamaño nuevo: la tabla no puede
 * resolverlo sola porque las filas no están en el navegador.
 */
export function paginacionServidor(opciones: OpcionesPaginacionServidor) {
  const {
    page,
    size,
    total,
    onChange,
    opciones: tamanos = OPCIONES_POR_DEFECTO,
    etiqueta,
  } = opciones;

  const [singular, plural] = Array.isArray(etiqueta)
    ? etiqueta
    : etiqueta
      ? [etiqueta, `${etiqueta}s`]
      : ["", ""];

  return {
    current: page,
    pageSize: size,
    total,
    // Se ofrece SIEMPRE, no solo cuando hay muchas filas. Condicionarlo a
    // `total > 10` hace que el control aparezca y desaparezca según el filtro
    // aplicado, y eso se lee como que la página está fallando.
    showSizeChanger: true,
    pageSizeOptions: tamanos.includes(size)
      ? tamanos
      : [...tamanos, size].sort((a, b) => a - b),
    showTotal: (t: number, rango: [number, number]) =>
      singular
        ? `${rango[0]}–${rango[1]} de ${t} ${t === 1 ? singular : plural}`
        : `${rango[0]}–${rango[1]} de ${t}`,
    onChange,
  };
}
